<script setup lang="ts">
import type { ProfileCustomerAddress, ProfileAddressRow } from '~/composables/useAuth';
import AreaRiservataAddressLookupSelect from '~/components/area-riservata/AddressLookupSelect.vue';
import { Checkbox } from '~/components/ui/checkbox';
import BaseConfirmDialog from '~/components/base/BaseConfirmDialog.vue';

const props = defineProps<{
	open: boolean;
	customerAddress: ProfileCustomerAddress | null;
}>();

const emit = defineEmits<{
	close: [];
	saved: [];
}>();

const { authFetch } = useAuthFetch();

const addressId = computed(() => props.customerAddress?.address?.id);

const label = ref('');
const isDefaultBilling = ref(false);
const isDefaultShipping = ref(false);
const street = ref('');
const countryId = ref<number | null>(null);
const regionId = ref<number | null>(null);
const provinceId = ref<number | null>(null);
const cityId = ref<number | null>(null);
const zipcodeId = ref<number | null>(null);

const countries = ref<{ id: number; name: string }[]>([]);
const regions = ref<{ id: number; name: string }[]>([]);
const provinces = ref<{ id: number; name: string }[]>([]);
const cities = ref<{ id: number; name: string }[]>([]);
const zipcodes = ref<{ id: number; label: string; locality: string; zipcode: string }[]>([]);

const loadingCountries = ref(false);
const loadingRegions = ref(false);
const loadingProvinces = ref(false);
const loadingCities = ref(false);
const loadingZipcodes = ref(false);
const showDeleteAddressDialog = ref(false);
const saving = ref(false);
const deleting = ref(false);
const error = ref<string | null>(null);
const initializing = ref(false);

const countrySelectRef = ref<InstanceType<typeof AreaRiservataAddressLookupSelect> | null>(null);
const regionSelectRef = ref<InstanceType<typeof AreaRiservataAddressLookupSelect> | null>(null);
const provinceSelectRef = ref<InstanceType<typeof AreaRiservataAddressLookupSelect> | null>(null);
const citySelectRef = ref<InstanceType<typeof AreaRiservataAddressLookupSelect> | null>(null);
const zipcodeSelectRef = ref<InstanceType<typeof AreaRiservataAddressLookupSelect> | null>(null);
const streetInputRef = ref<HTMLInputElement | null>(null);

async function fetchCountries() {
	loadingCountries.value = true;
	try {
		const data = await authFetch<{ id: number; name: string }[]>('/api/area-riservata/address-lookup/countries');
		countries.value = Array.isArray(data) ? data : [];
	} finally {
		loadingCountries.value = false;
	}
}

async function fetchRegions() {
	if (countryId.value == null) {
		regions.value = [];
		return;
	}
	loadingRegions.value = true;
	try {
		const data = await authFetch<{ id: number; name: string }[]>(
			`/api/area-riservata/address-lookup/regions?country=${countryId.value}`
		);
		regions.value = Array.isArray(data) ? data : [];
	} finally {
		loadingRegions.value = false;
	}
}

async function fetchProvinces() {
	if (regionId.value == null) {
		provinces.value = [];
		return;
	}
	loadingProvinces.value = true;
	try {
		const data = await authFetch<{ id: number; name: string }[]>(
			`/api/area-riservata/address-lookup/provinces?region=${regionId.value}`
		);
		provinces.value = Array.isArray(data) ? data : [];
	} finally {
		loadingProvinces.value = false;
	}
}

async function fetchCities() {
	if (provinceId.value == null) {
		cities.value = [];
		return;
	}
	loadingCities.value = true;
	try {
		const data = await authFetch<{ id: number; name: string }[]>(
			`/api/area-riservata/address-lookup/cities?province=${provinceId.value}`
		);
		cities.value = Array.isArray(data) ? data : [];
	} finally {
		loadingCities.value = false;
	}
}

async function fetchZipcodes() {
	if (cityId.value != null) {
		loadingZipcodes.value = true;
		try {
			const data = await authFetch<{ id: number; label: string; locality: string; zipcode: string }[]>(
				`/api/area-riservata/address-lookup/zipcodes?city=${cityId.value}`
			);
			zipcodes.value = Array.isArray(data) ? data : [];
		} finally {
			loadingZipcodes.value = false;
		}
	} else if (provinceId.value != null) {
		loadingZipcodes.value = true;
		try {
			const data = await authFetch<{ id: number; label: string; locality: string; zipcode: string }[]>(
				`/api/area-riservata/address-lookup/zipcodes?province=${provinceId.value}`
			);
			zipcodes.value = Array.isArray(data) ? data : [];
		} finally {
			loadingZipcodes.value = false;
		}
	} else {
		zipcodes.value = [];
	}
}

const zipcodeOptions = computed(() =>
	zipcodes.value.map((z) => ({ id: z.id, label: z.label, name: z.label }))
);

watch(
	() => [props.open, props.customerAddress] as const,
	async ([open, ca]) => {
		if (open && ca?.address) {
			initializing.value = true;
			error.value = null;
			label.value = ca.label ?? '';
			isDefaultBilling.value = !!ca.is_default_billing;
			isDefaultShipping.value = !!ca.is_default_shipping;
			const a = ca.address;
			street.value = a.street ?? '';
			countryId.value = (a.country && typeof a.country === 'object' && a.country.id) ? a.country.id : null;
			regionId.value = (a.region && typeof a.region === 'object' && a.region.id) ? a.region.id : null;
			provinceId.value = (a.province && typeof a.province === 'object' && a.province.id) ? a.province.id : null;
			cityId.value = (a.city && typeof a.city === 'object' && a.city.id) ? a.city.id : null;
			zipcodeId.value = (a.zipcode && typeof a.zipcode === 'object' && a.zipcode.id) ? a.zipcode.id : null;
			countries.value = [];
			regions.value = [];
			provinces.value = [];
			cities.value = [];
			zipcodes.value = [];
			await fetchCountries();
			if (countryId.value != null) await fetchRegions();
			if (regionId.value != null) await fetchProvinces();
			if (provinceId.value != null) await fetchCities();
			if (cityId.value != null || provinceId.value != null) await fetchZipcodes();
			initializing.value = false;
		}
	},
	{ immediate: true }
);

watch(countryId, (val, oldVal) => {
	if (initializing.value) return;
	if (val !== oldVal) {
		regionId.value = null;
		provinceId.value = null;
		cityId.value = null;
		zipcodeId.value = null;
		regions.value = [];
		provinces.value = [];
		cities.value = [];
		zipcodes.value = [];
		if (val != null) fetchRegions();
	}
});

watch(regionId, (val, oldVal) => {
	if (initializing.value) return;
	if (val !== oldVal) {
		provinceId.value = null;
		cityId.value = null;
		zipcodeId.value = null;
		provinces.value = [];
		cities.value = [];
		zipcodes.value = [];
		if (val != null) fetchProvinces();
	}
});

watch(provinceId, (val, oldVal) => {
	if (initializing.value) return;
	if (val !== oldVal) {
		cityId.value = null;
		zipcodeId.value = null;
		cities.value = [];
		zipcodes.value = [];
		if (val != null) {
			fetchCities();
			fetchZipcodes();
		}
	}
});

watch(cityId, (val, oldVal) => {
	if (initializing.value) return;
	if (val !== oldVal) {
		zipcodeId.value = null;
		zipcodes.value = [];
	}
	if (val != null || provinceId.value != null) fetchZipcodes();
});

async function save() {
	const id = addressId.value;
	if (id == null) return;
	error.value = null;
	saving.value = true;
	try {
		await authFetch(`/api/area-riservata/addresses/${id}`, {
			method: 'PATCH',
			body: {
				street: street.value || null,
				country: countryId.value,
				region: regionId.value,
				province: provinceId.value,
				city: cityId.value,
				zipcode: zipcodeId.value,
				label: label.value || null,
				is_default_billing: isDefaultBilling.value,
				is_default_shipping: isDefaultShipping.value,
			},
		});
		emit('saved');
	} catch (err: any) {
		error.value = err?.data?.statusMessage || err?.message || 'Errore durante il salvataggio';
	} finally {
		saving.value = false;
	}
}

async function removeAddress() {
	const id = addressId.value;
	if (id == null || deleting.value) return;
	error.value = null;
	deleting.value = true;
	try {
		await authFetch(`/api/area-riservata/addresses/${id}`, { method: 'DELETE' });
		emit('saved');
	} catch (err: any) {
		error.value = err?.data?.statusMessage || err?.message || 'Errore durante l\'eliminazione';
	} finally {
		deleting.value = false;
	}
}

function close() {
	emit('close');
}

function formatAddressLine(addr: ProfileAddressRow | null | undefined): string {
	if (!addr) return '—';
	const parts: string[] = [];
	if (addr.street) parts.push(addr.street);
	const city = typeof addr.city === 'object' && addr.city?.name ? addr.city.name : null;
	const zip = typeof addr.zipcode === 'object' && addr.zipcode?.locality ? addr.zipcode.locality : null;
	if (zip && city) parts.push(`${zip} ${city}`);
	else if (city) parts.push(city);
	const province = typeof addr.province === 'object' && addr.province?.name ? addr.province.name : null;
	if (province) parts.push(province);
	const country = typeof addr.country === 'object' && addr.country?.name ? addr.country.name : null;
	if (country) parts.push(country);
	return parts.length ? parts.join(', ') : '—';
}
</script>

<template>
	<Teleport to="body">
		<div
			v-if="open && customerAddress"
			class="fixed inset-0 z-50 flex"
			aria-modal="true"
			role="dialog"
			aria-labelledby="panel-indirizzo-title"
		>
			<button
				type="button"
				class="absolute inset-0 bg-black/50 transition-opacity"
				aria-label="Chiudi"
				@click="close"
			/>
			<div
				class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
				@click.stop
			>
				<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
					<h2 id="panel-indirizzo-title" class="text-lg font-semibold text-gray-900">
						Modifica indirizzo
					</h2>
					<button
						type="button"
						class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
						aria-label="Chiudi"
						@click="close"
					>
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>
				<div class="flex-1 overflow-y-auto px-4 py-4">
					<!-- Card indirizzo come in elenco -->
					<div class="mb-4 flex flex-col gap-1 rounded-none border border-gray-100 bg-gray-50/50 p-3">
						<div class="flex flex-wrap items-center gap-2">
							<span v-if="customerAddress.label" class="text-sm font-medium text-gray-900">{{ customerAddress.label }}</span>
							<span
								v-if="customerAddress.is_default_billing"
								class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-800"
							>
								Fatturazione
							</span>
							<span
								v-if="customerAddress.is_default_shipping"
								class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-800"
							>
								Spedizione
							</span>
						</div>
						<p class="text-sm text-gray-700">{{ formatAddressLine(customerAddress.address) }}</p>
					</div>

					<form class="space-y-4" @submit.prevent="save">
						<div v-if="error" class="rounded-md bg-red-50 p-3 text-sm text-red-800">
							{{ error }}
						</div>

						<div>
							<label for="edit-address-label" class="block text-sm font-medium text-gray-700 mb-1">Etichetta (opzionale)</label>
							<input
								id="edit-address-label"
								v-model="label"
								type="text"
								placeholder="es. Casa, Ufficio"
								class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#A21B1F] focus:ring-[#A21B1F] sm:text-sm"
							/>
						</div>

						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Stato</label>
							<AreaRiservataAddressLookupSelect
								ref="countrySelectRef"
								v-model="countryId"
								:options="countries"
								:loading="loadingCountries"
								placeholder="Seleziona stato"
								@advance="() => regionSelectRef?.focus()"
							/>
						</div>

						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Regione</label>
							<AreaRiservataAddressLookupSelect
								ref="regionSelectRef"
								v-model="regionId"
								:options="regions"
								:loading="loadingRegions"
								:disabled="countryId == null"
								placeholder="Seleziona regione"
								@advance="() => provinceSelectRef?.focus()"
							/>
						</div>

						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
							<AreaRiservataAddressLookupSelect
								ref="provinceSelectRef"
								v-model="provinceId"
								:options="provinces"
								:loading="loadingProvinces"
								:disabled="regionId == null"
								placeholder="Seleziona provincia"
								@advance="() => citySelectRef?.focus()"
							/>
						</div>

						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Comune</label>
							<AreaRiservataAddressLookupSelect
								ref="citySelectRef"
								v-model="cityId"
								:options="cities"
								:loading="loadingCities"
								:disabled="provinceId == null"
								placeholder="Seleziona comune"
								@advance="() => zipcodeSelectRef?.focus()"
							/>
						</div>

						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">CAP / Località</label>
							<AreaRiservataAddressLookupSelect
								ref="zipcodeSelectRef"
								v-model="zipcodeId"
								:options="zipcodeOptions"
								:loading="loadingZipcodes"
								:disabled="provinceId == null && cityId == null"
								placeholder="Seleziona CAP o località"
								@advance="() => streetInputRef?.focus()"
							/>
						</div>

						<div>
							<label for="indirizzo-street" class="block text-sm font-medium text-gray-700 mb-1">Via / Indirizzo</label>
							<input
								id="indirizzo-street"
								ref="streetInputRef"
								v-model="street"
								type="text"
								autocomplete="street-address"
								class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#A21B1F] focus:ring-[#A21B1F] sm:text-sm"
							/>
						</div>

						<div class="flex flex-wrap gap-6 border-t border-gray-200 pt-4">
							<label class="inline-flex items-center gap-2 cursor-pointer">
								<Checkbox
									:checked="isDefaultBilling"
									@update:checked="(v: boolean) => (isDefaultBilling = v)"
									class="border-gray-300 data-[state=checked]:bg-[#A21B1F] data-[state=checked]:text-white data-[state=checked]:border-[#A21B1F] focus-visible:ring-[#A21B1F]"
								/>
								<span class="text-sm text-gray-700">Predefinito fatturazione</span>
							</label>
							<label class="inline-flex items-center gap-2 cursor-pointer">
								<Checkbox
									:checked="isDefaultShipping"
									@update:checked="(v: boolean) => (isDefaultShipping = v)"
									class="border-gray-300 data-[state=checked]:bg-[#A21B1F] data-[state=checked]:text-white data-[state=checked]:border-[#A21B1F] focus-visible:ring-[#A21B1F]"
								/>
								<span class="text-sm text-gray-700">Predefinito spedizione</span>
							</label>
						</div>

						<div class="flex items-center justify-between border-t border-gray-200 pt-4">
							<button
								type="button"
								class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-[#A21B1F] hover:bg-[#A21B1F]/90 rounded-none transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
								:disabled="deleting"
								@click="showDeleteAddressDialog = true"
							>
								{{ deleting ? 'Eliminazione...' : 'Elimina' }}
							</button>
							<div class="flex gap-3">
								<button
									type="button"
									class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-black bg-white border-2 border-black hover:bg-gray-50 hover:text-[#A21B1F] rounded-none transition-colors uppercase"
									@click="close"
								>
									Annulla
								</button>
								<button
									type="submit"
									:disabled="saving"
									class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-black hover:bg-black/90 rounded-none transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{{ saving ? 'SALVATAGGIO...' : 'SALVA' }}
								</button>
							</div>
						</div>
					</form>
				</div>
			</div>
		</div>
	</Teleport>

	<BaseConfirmDialog
		v-model="showDeleteAddressDialog"
		title="Elimina indirizzo"
		description="Sei sicuro di voler eliminare questo indirizzo? L'operazione non può essere annullata."
		confirm-label="ELIMINA INDIRIZZO"
		@confirm="removeAddress"
	/>
</template>
